import Loading from '@ui/loading';
import { type FC } from 'react';

import styles from './index.module.scss';
import Footer from '@components/footer';
import { useNavigate } from 'react-router-dom';
import { usePumpRankingChanges } from '@hooks/use-pump-ranking-changes';
import Lottie from 'lottie-react';
import bannerGainersJSON from '@assets/lottie/pump/banner-gainers.json';
import Top from '@components/top';
import { rankingIcons } from '@constants/icons';
import Avatar from '@components/avatar';
import { timeDifference } from '@utils/time-difference';
import { formatDigitUnit } from '@utils/format-digit-unit';
import Buy from '@components/buy';
import { useWindowSize } from 'react-use';
import PumpItemMobile from '@components/pump-item-mobile';
import type { OpenapiClientPump } from 'src/openapi/pump';
import { useSuiNSName } from '@hooks/use-sui-ns-name';

const Gainers: FC = () => {
  const { width } = useWindowSize();
  const rankingChanges = usePumpRankingChanges(20);

  return (
    <>
      <Top />
      <div className={styles.wrapper}>
        <div className={styles.banner}>
          <div className={styles.banner_text}>
            <div className={styles.banner_text1}>Top Gainers</div>
            <div className={styles.banner_text2}>24h Gainers</div>
          </div>
          <div className={styles.banner_img}>
            <Lottie
              animationData={bannerGainersJSON}
              autoplay
              loop={false}
              className={styles.lottie}
            />
          </div>
        </div>
        <div className={styles.content}>
          {width < 640 ? (
            <div>
              {rankingChanges.loading && (
                <div className={styles.loading}>
                  <Loading />
                </div>
              )}
              {rankingChanges.rankingChanges?.map((item, index) => (
                <PumpItemMobile item={item} index={index} key={index} />
              ))}
            </div>
          ) : (
            <div>
              <div className={styles.items}>
                <ul className={`${styles.th}`}>
                  <li>Token</li>
                  <li>Created by</li>
                  <li className={styles.two}>Market Cap</li>
                  <li className={styles.two}>Increase</li>
                </ul>

                {rankingChanges.loading && (
                  <div className={styles.loading}>
                    <Loading />
                  </div>
                )}

                {rankingChanges.rankingChanges?.map((item, index) => (
                  <List item={item} index={index} key={item.token_address} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const List: FC<{
  item: OpenapiClientPump.GetRankingsChangesResponse[number];
  index: number;
}> = ({ item, index }) => {
  const navigate = useNavigate();
  const suiNames = useSuiNSName(item.created_by);

  const icon = rankingIcons[index];

  return (
    <ul
      className={`${styles.td}`}
      key={item.token_address}
      onClick={() => navigate(`/fun/${item.token_address}`)}
    >
      <li>
        <div className={`${styles.ranking} ${styles['ranking' + index]}`}>
          <span>{icon ? <img src={icon} /> : index + 1}</span>
          <img src={item.token_metadata.iconUrl} />
          <div className={styles.name_symbol}>
            <p>{item.token_metadata.name} </p>
            <p>${item.token_metadata.symbol}</p>
          </div>
        </div>
      </li>
      <li>
        <div className={styles.create}>
          <Avatar address={item.created_by} className={styles.create_icon} />
          <div className={styles.create_con}>
            <div className={styles.create_sender}>
              {suiNames || item.created_by.slice(item.created_by.length - 8)}
            </div>
            <div className={styles.create_time}>{timeDifference(item.deployed_at, 1)} ago</div>
          </div>
        </div>
      </li>
      <li>
        <div className={styles.market_value}>${formatDigitUnit(item.market_cap_usd, 2)}</div>
        <div className={`${styles.market_change} ${item.change_24h >= 0 ? '' : styles.neg}`}>
          +{Number(item.change_24h.toFixed(2))}%
        </div>
      </li>
      <li className={styles.changes}>
        <Buy
          token_address={item.token_address}
          name={item.token_metadata.name}
          symbol={item.token_metadata.symbol}
          iconUrl={item.token_metadata.iconUrl}
        />
      </li>
    </ul>
  );
};

export default Gainers;
